<template>
  <tab-layout :full-width="true">
    <form
      slot="tabContent"
      class="row mx-0"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div class="col px-0 pr-lg-3">
        <div class="bg-white rounded mb-4 p-3">
          <page-heading>Edit Event</page-heading>

          <div v-if="loading || loadingTypes" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>

          <event-form
            v-else-if="!loadFailed"
            :key="formKey"
            :events="events"
            :shared-form="sharedForm"
            :event-types="eventTypes"
            :loading-types="false"
            :is-combined="isCombined"
            :brother-id="brotherId"
            :lock-toggles="true"
            :errors="errors"
            :alert="{ message: alert.message.value, type: alert.type.value }"
            :sponsor-logo-url="sharedForm.sponsor_logo_url"
            :deleting-logo="deletingLogo"
            :available-judges="availableJudges"
            :loading-judges="loadingJudges"
            :judge-ids="judgeIds"
            @toggle-combined="onCombinedToggle"
            @update-shared="onUpdateShared"
            @update-event="updateEvent"
            @logo-change="onLogoChange"
            @remove-logo="deleteSponsorLogo"
            @update-judge-ids="updateJudgeSelection"
            @search-judges="searchJudges"
          />

          <!-- Mobile submit -->
          <mobile-submit-button :loading="submitting">
            Save Event
          </mobile-submit-button>
        </div>
      </div>

      <!-- Desktop action bar -->
      <desktop-action-bar>
        <app-button type="submit" class="px-md-5" :loading="submitting">
          Save Event
        </app-button>
      </desktop-action-bar>

    </form>
  </tab-layout>
</template>

<script>
import { defineComponent, onMounted, ref, getCurrentInstance } from '@vue/composition-api'
import TabLayout from '@/components/TabLayout'
import PageHeading from '@/components/PageHeading'
import EventBus from '@/store/EventBus'
import EventForm from '@/components/Events/EventForm'
import MobileSubmitButton from '@/components/MobileSubmitButton'
import AppButton from '@/components/AppButton'
import DesktopActionBar from '@/components/DesktopActionBar'
import useEventEdit from '@/composables/events/useEventEdit'

export default defineComponent({
  name: 'SecretaryEventEdit',
  components: { TabLayout, PageHeading, EventForm, MobileSubmitButton, AppButton, DesktopActionBar },
  props: {
    draftId: { type: [String, Number], required: true },
    eventId: { type: [String, Number], required: true }
  },
  setup(props) {
    const vm = getCurrentInstance()
    const proxy = vm.proxy

    const {
      eventTypes,
      loadingTypes,
      loading,
      loadFailed,
      isCombined,
      brotherId,
      events,
      sharedForm,
      errors,
      submitting,
      deletingLogo,
      alert,
      availableJudges,
      loadingJudges,
      judgeIds,
      loadEventTypes,
      loadEvent,
      reloadEvent,
      updateEvent,
      searchJudges,
      updateJudgeSelection,
      restoreLegacySelectedJudges,
      deleteSponsorLogo,
      submit
    } = useEventEdit(props.draftId, props.eventId)

    const formKey = ref(0)

    onMounted(async () => {
      proxy.$nextTick(() => {
        proxy.$root.$emit('update-breadcrumb', { name: 'Committee', link: `committees/dashboard/${props.draftId}` })
        proxy.$root.$emit('update-breadcrumb', { name: 'View Draft', link: `drafts/view/${props.draftId}` })
      })
      await Promise.all([loadEventTypes(), loadEvent(), searchJudges('')])
      restoreLegacySelectedJudges()
    })

    function onCombinedToggle(val) {
      isCombined.value = val
      if (!val) {
        sharedForm.is_conjunction = false
        sharedForm.discount_price = ''
      }
    }

    function onUpdateShared(field, val) {
      sharedForm[field] = val
    }

    function onLogoChange(file) {
      sharedForm.sponsor_logo = file
    }

    async function handleSubmit() {
      const message = await submit()
      if (message) {
        await reloadEvent()
        restoreLegacySelectedJudges()
        formKey.value += 1
        EventBus.$emit('show-success', typeof message === 'string' ? message : 'The event has been saved.')
      }
    }

    return {
      eventTypes,
      loadingTypes,
      loading,
      loadFailed,
      isCombined,
      brotherId,
      events,
      sharedForm,
      errors,
      submitting,
      deletingLogo,
      alert,
      availableJudges,
      loadingJudges,
      judgeIds,
      formKey,
      updateEvent,
      searchJudges,
      updateJudgeSelection,
      restoreLegacySelectedJudges,
      deleteSponsorLogo,
      onCombinedToggle,
      onUpdateShared,
      onLogoChange,
      handleSubmit
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Drafts/Events/SecretaryEventEdit.vue